/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Logger } from '@egw/logger'
import { configClient } from '@egw/config'
import { Cookies } from '@egw/clients'
import { useAuth } from '@egw/auth'
import { useFlags } from '@egw/feature-flags'
import { type FeatureFlags } from '@egw/constants'
import { type Config } from '../types'

const log = new Logger('VGS Show')

declare global {
  interface Window {
    VGSShow: any
  }
}

export enum EVGSRevealStatus {
  INITIAL = 'INITIAL',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
  LOADING = 'LOADING',
}

export enum ECardDetail {
  CARD_NUMBER = 'CARD_NUMBER',
  EXPIRATION_DATE = 'EXPIRATION_DATE',
  CVV = 'CVV',
}

export interface TRenderCardArgs {
  wrapper: React.RefObject<HTMLDivElement>
  styles: object
  cardId: string
  jsonPath: string
  path: string
  cardDetail: ECardDetail
  payload?: object
}

interface VgsShowContextType {
  showCard: boolean
  cardRevealed: boolean
  toggleShowCard: () => void
  renderCardValue?: (args: TRenderCardArgs) => Promise<void>
  isLoading: boolean
  isSuccess: boolean
  showError: boolean
  anyInitial: boolean
}

const VGS_REVEAL_INIT = {
  [ECardDetail.CVV]: EVGSRevealStatus.INITIAL,
  [ECardDetail.CARD_NUMBER]: EVGSRevealStatus.INITIAL,
  [ECardDetail.EXPIRATION_DATE]: EVGSRevealStatus.INITIAL,
}

const VGS_ALL_SUCCESS = {
  [ECardDetail.CVV]: EVGSRevealStatus.SUCCESS,
  [ECardDetail.CARD_NUMBER]: EVGSRevealStatus.SUCCESS,
  [ECardDetail.EXPIRATION_DATE]: EVGSRevealStatus.SUCCESS,
}

const VgsShowContext = createContext<VgsShowContextType>({
  showCard: false,
  cardRevealed: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggleShowCard: () => {},
  renderCardValue: undefined,
  isLoading: false,
  isSuccess: false,
  showError: false,
  anyInitial: true,
})

export const useVgsShow = () => useContext(VgsShowContext)

interface VgsShowContextProviderProps {
  children: ReactNode
}

export const VgsShowContextProvider = ({ children }: VgsShowContextProviderProps) => {
  const [showCard, setShowCard] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [previouslyRevealed, setPreviouslyRevealed] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { accessToken } = useAuth()
  const { flags } = useFlags<FeatureFlags>()

  const isVGSShowLoaded = useVGSShow()

  const config = configClient.getConfig() as Config

  const [vgsRevealStatus, setVgsRevealStatus] = useState<Record<ECardDetail, EVGSRevealStatus>>(VGS_REVEAL_INIT)

  const viewCard = useCallback(() => {
    setShowError(false)
    setShowCard(true)
    setVgsRevealStatus(VGS_REVEAL_INIT)
  }, [setShowCard])

  const hideCard = useCallback(() => {
    setShowCard(false)
    setVgsRevealStatus(VGS_REVEAL_INIT)
  }, [])

  const toggleShowCard = showCard ? hideCard : viewCard

  const updateVgsRevealStatus = useCallback(
    ({ cardDetail, status }: { cardDetail: ECardDetail; status: EVGSRevealStatus }) => {
      setVgsRevealStatus((prev) => ({ ...prev, [cardDetail]: status }))
    },
    [],
  )

  const makeShow = async () => {
    if (typeof window === 'undefined') return

    if (isVGSShowLoaded && window.VGSShow?.create) {
      const show = window.VGSShow?.create(config.vgs.vgsVaultId, (state: unknown) => {
        log.info(state)
      })
        .setEnvironment(config.vgs.vgsEnv)
        .setCname(config.vgs.vgsCName)

      return show
    }
  }

  // call VGS and let the SDK generate the iframe

  const makeVGSRequest = function (vgs: any, cardId: string, jsonPath: string, path: string, payload: any): any {
    log.info('sending vgs request', jsonPath, path, payload)
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    }

    return vgs.request({
      name: cardId,
      method: 'POST',
      path,
      payload,
      serializers: [vgs.SERIALIZERS.replace('(\\d{4})(\\d{4})(\\d{4})(\\d{4})', '$1 $2 $3 $4')],
      headers,
      htmlWrapper: 'text',
      jsonPathSelector: jsonPath,
    })
  }

  const renderCardValue = async function ({
    wrapper,
    styles,
    cardId,
    jsonPath,
    path,
    payload,
    cardDetail,
  }: TRenderCardArgs) {
    log.info('VGSShow render initializing for: ', cardDetail)
    // If revealed set all state to SUCCESS
    if (previouslyRevealed) {
      setVgsRevealStatus(VGS_ALL_SUCCESS)
      return
    }

    updateVgsRevealStatus({ cardDetail, status: EVGSRevealStatus.LOADING })

    const vgs = await makeShow()

    if (vgs) {
      const frame = await makeVGSRequest(vgs, cardId, jsonPath, path, {
        cardId,
        ...payload,
      })

      // renders the VGS iframe
      frame.render(wrapper.current, styles)

      // callbacks on certain statuses
      frame.on('requestSuccess', () => {
        log.info(`VGS ${cardDetail} request success.`)
      })
      frame.on('requestFail', () => {
        log.info(`VGS ${cardDetail} request fail.`)
        updateVgsRevealStatus({ cardDetail, status: EVGSRevealStatus.FAIL })
      })
      frame.on('revealSuccess', () => {
        log.info(`VGS ${cardDetail} reveal success.`)
        updateVgsRevealStatus({ cardDetail, status: EVGSRevealStatus.SUCCESS })
      })
      frame.on('revealFail', () => {
        log.info(`VGS ${cardDetail} reveal fail.`)
        updateVgsRevealStatus({ cardDetail, status: EVGSRevealStatus.FAIL })
      })
    }
  }

  const { isLoading, isSuccess, isError, anyInitial } = useMemo(
    () => ({
      ...checkLoadingAndErrors(vgsRevealStatus, showCard),
      ...checkAllSuccessful(vgsRevealStatus, showCard),
      ...checkAnyInitial(vgsRevealStatus, showCard),
    }),
    [vgsRevealStatus, showCard],
  )

  const cardRevealed = useMemo(() => showCard && isSuccess, [showCard, isSuccess])

  useEffect(() => {
    // If any of 3 iFrames are not initial && not loading && an error is present show the error
    // Essentiall doesn't show till all frames have resolved.
    if (!anyInitial && !isLoading && isError) {
      setShowError(true)
      // Reset show / hide card if it's resolved to an error
      setShowCard(false)
    }
    // Set revealed, keep track in state
    if (cardRevealed && !previouslyRevealed) {
      setPreviouslyRevealed(true)
    }
  }, [isError, anyInitial, isLoading, cardRevealed, previouslyRevealed])

  const value: VgsShowContextType = {
    showCard,
    cardRevealed,
    toggleShowCard,
    renderCardValue,
    isLoading,
    isSuccess,
    showError,
    anyInitial,
  }

  return <VgsShowContext.Provider value={value}>{children}</VgsShowContext.Provider>
}

export const checkLoadingAndErrors = (vgsRevealStatus: Record<ECardDetail, EVGSRevealStatus>, showCard: boolean) =>
  Object.values(vgsRevealStatus).reduce(
    (acc, curr) => {
      if (curr === EVGSRevealStatus.FAIL && showCard) {
        acc.isError = true
      }
      if (curr === EVGSRevealStatus.LOADING && showCard) {
        acc.isLoading = true
      }
      return acc
    },
    { isLoading: false, isError: false },
  )

export const checkAllSuccessful = (vgsRevealStatus: Record<ECardDetail, EVGSRevealStatus>, showCard: boolean) =>
  Object.values(vgsRevealStatus).reduce(
    (acc, curr) => {
      // If any status is not successful, isSuccess will return as false
      if (curr !== EVGSRevealStatus.SUCCESS && showCard) {
        acc.isSuccess = false
      }
      return acc
    },
    { isSuccess: true },
  )

export const checkAnyInitial = (vgsRevealStatus: Record<ECardDetail, EVGSRevealStatus>, showCard: boolean) =>
  Object.values(vgsRevealStatus).reduce(
    (acc, curr) => {
      if (curr === EVGSRevealStatus.INITIAL && showCard) {
        acc.anyInitial = true
      }
      return acc
    },
    { anyInitial: false },
  )

const loadVGSShowScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src="https://js.verygoodvault.com/vgs-show/2.1.0/show.js"]')
    if (existingScript) {
      console.log('VGSShow script already loaded')
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://js.verygoodvault.com/vgs-show/2.1.0/show.js'
    script.onload = () => {
      resolve()
    }
    script.onerror = () => {
      reject(new Error('Failed to load VGSShow script'))
    }
    document.body.appendChild(script)
  })
}

const useVGSShow = () => {
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    loadVGSShowScript()
      .then(() => {
        if (window.VGSShow) {
          console.log('VGSShow SDK loaded successfully')
          setIsLoaded(true)
        } else {
          console.error('VGSShow is not available on window')
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }, [])

  return isLoaded
}
